import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Clock, RefreshCw, Check, Loader2, X, Mail, MessageCircle, QrCode, FileText, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const FRONTEND_URL = window.location.origin;

export default function MobileUploadQR({ open, onOpenChange, patientId, patientName, clinicalNoteId }) {
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState('');
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Cache del último número de archivos vistos → si en un poll aparece un
  // archivo nuevo, dispara `medical-file:changed` para que la HC recargue
  // sin esperar a cerrar el diálogo. `null` = aún no inicializado (primer
  // fetch); así evitamos disparar el evento con el histórico previo, que
  // provocaba que ClinicalHistoryPage recargase y desmontase la Galería,
  // cerrando este mismo diálogo (bug 16-feb-2026).
  const lastCountRef = useRef(null);

  const loadHistory = async () => {
    if (!patientId) return;
    setHistoryLoading(true);
    try {
      const r = await api.get(`/mobile-upload/history/${patientId}`);
      const items = r.data?.items || [];
      setHistory(items);
      if (lastCountRef.current === null) {
        // Baseline en el primer fetch — a partir de aquí sólo disparamos si sube.
        lastCountRef.current = items.length;
      } else if (items.length > lastCountRef.current) {
        window.dispatchEvent(new CustomEvent('medical-file:changed', { detail: { patientId } }));
        lastCountRef.current = items.length;
      }
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open && !token) generateToken();
    if (open) {
      // Reset del baseline cada vez que se abre el diálogo — así detectamos
      // sólo subidas OCURRIDAS mientras está abierto.
      lastCountRef.current = null;
      loadHistory();
      // Poll every 4 s mientras el modal está abierto — el paciente sube
      // desde el móvil y el usuario ve aparecer las miniaturas.
      const pollId = setInterval(loadHistory, 4000);
      return () => clearInterval(pollId);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      if (diff <= 0) { setTimeLeft('Caducado'); setExpired(true); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${m}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generateToken = async () => {
    setLoading(true);
    setExpired(false);
    try {
      const payload = { patient_id: patientId };
      // Si el QR se abre desde dentro de una nota clínica concreta, la
      // preseleccionamos como default en el móvil para que la paciente no
      // tenga que buscarla en el desplegable — ver MobileUploadPage.jsx.
      if (clinicalNoteId) payload.clinical_note_id = clinicalNoteId;
      const res = await api.post('/mobile-upload/token', payload);
      setToken(res.data.token);
      setExpiresAt(res.data.expires_at);
    } catch {
      toast.error('Error al generar enlace');
    } finally {
      setLoading(false);
    }
  };

  const extendToken = async () => {
    if (!token) return;
    try {
      const res = await api.post(`/mobile-upload/extend/${token}`);
      setExpiresAt(res.data.expires_at);
      setExpired(false);
      toast.success('Tiempo extendido 15 minutos');
    } catch {
      toast.error('Error al extender');
    }
  };

  const handleClose = () => {
    setToken(null);
    setExpiresAt(null);
    setExpired(false);
    setSending('');
    onOpenChange(false);
  };

  // Send a longer-lived (48h) upload link to the patient by email or
  // WhatsApp so they can upload from home without being at the clinic.
  const [sending, setSending] = useState(''); // '' | 'email' | 'whatsapp'
  // The WhatsApp channel is only usable when Meta has approved the
  // `remote_upload_link` template.  Until then we disable the button
  // (AGENT_RULES.md · Rule 2.1 — free-text WhatsApp is forbidden).
  const [waTemplateApproved, setWaTemplateApproved] = useState(null); // null=loading, true, false
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get('/mobile-upload/remote-link-whatsapp-status');
        if (alive) setWaTemplateApproved(!!r.data?.approved);
      } catch {
        if (alive) setWaTemplateApproved(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);
  const sendRemote = async (channel) => {
    if (sending) return;
    setSending(channel);
    try {
      const r = await api.post('/mobile-upload/send-remote', { patient_id: patientId, channel });
      toast.success(
        channel === 'email'
          ? `Email enviado a ${r.data.recipient}`
          : `WhatsApp enviado a ${r.data.recipient}`
      );
      // Refresh history so the new "sent" row appears immediately.
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el enlace');
    } finally {
      setSending('');
    }
  };

  const uploadUrl = token ? `${FRONTEND_URL}/mobile-upload/${token}` : '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-brand-navy">
            <Smartphone size={20} />
            Subir desde Movil
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Escanea este QR con tu movil para subir fotos y archivos directamente a la historia de <strong>{patientName}</strong>.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-navy" />
            </div>
          ) : token ? (
            <div className="flex flex-col items-center gap-4">
              {/* QR Code */}
              <div className={`p-4 bg-white rounded-2xl border-2 ${expired ? 'border-red-300 opacity-50' : 'border-brand-medium'} shadow-sm`}>
                <QRCodeSVG
                  value={uploadUrl}
                  size={220}
                  level="M"
                  includeMargin={false}
                  data-testid="mobile-upload-qr"
                />
              </div>

              {/* Timer */}
              <div className={`flex items-center gap-2 text-sm font-medium ${expired ? 'text-red-600' : 'text-slate-600'}`}>
                <Clock size={16} />
                {expired ? 'Enlace caducado' : `Valido por ${timeLeft}`}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                {expired ? (
                  <Button onClick={generateToken} className="bg-brand-navy hover:bg-brand-navy" data-testid="regenerate-qr-btn">
                    <RefreshCw size={16} className="mr-2" />
                    Generar nuevo QR
                  </Button>
                ) : (
                  <Button onClick={extendToken} variant="outline" size="sm" data-testid="extend-qr-btn">
                    <RefreshCw size={16} className="mr-2" />
                    Ampliar tiempo
                  </Button>
                )}
              </div>

              {!expired && (
                <p className="text-xs text-slate-400 text-center max-w-xs">
                  El enlace caduca en 15 minutos por seguridad. Puedes ampliarlo o generar uno nuevo.
                </p>
              )}
            </div>
          ) : null}

          {/* Envío remoto — para pacientes que suben desde casa.
              Crea un token independiente con TTL de 48h y lo entrega
              directamente al email/WhatsApp del paciente sin necesidad
              de que estén físicamente en la consulta. */}
          <div className="pt-3 border-t border-brand-medium/40">
            <p className="text-xs font-semibold text-brand-navy mb-1">O envíaselo remotamente</p>
            <p className="text-[11px] text-slate-500 mb-2">
              El paciente lo recibirá y podrá subir los archivos desde casa. El enlace remoto caduca en 48 horas.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendRemote('email')}
                disabled={!!sending}
                className="border-brand-medium text-brand-navy hover:bg-brand-soft/40"
                data-testid="send-remote-email-btn"
              >
                {sending === 'email' ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Enviando…</>
                ) : (
                  <><Mail size={14} className="mr-1.5" /> Enviar por Email</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => sendRemote('whatsapp')}
                disabled={!!sending || waTemplateApproved !== true}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                data-testid="send-remote-whatsapp-btn"
                title={
                  waTemplateApproved === false
                    ? 'Plantilla WhatsApp pendiente de aprobación de Meta. Envíelo por email de momento.'
                    : waTemplateApproved === null
                      ? 'Comprobando estado de plantilla…'
                      : ''
                }
              >
                {sending === 'whatsapp' ? (
                  <><Loader2 size={14} className="mr-1.5 animate-spin" /> Enviando…</>
                ) : (
                  <><MessageCircle size={14} className="mr-1.5" /> WhatsApp</>
                )}
              </Button>
            </div>
            {waTemplateApproved === false && (
              <p
                className="text-[10px] text-amber-700 mt-1.5 flex items-start gap-1"
                data-testid="wa-template-pending-notice"
              >
                <span aria-hidden="true">⏳</span>
                <span>
                  El envío por WhatsApp está pendiente de aprobación de Meta.
                  Mientras tanto, usa el envío por email.
                </span>
              </p>
            )}
          </div>

          {/* Historial de enlaces enviados — muestra los últimos 20 tokens
              generados para este paciente para saber si un enlace ya fue
              usado, caducó, o si el paciente subió los archivos. */}
          <div className="pt-3 border-t border-brand-medium/40" data-testid="upload-links-history">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-semibold text-brand-navy flex items-center gap-1">
                <History size={12} /> Historial de enlaces
              </p>
              <button
                type="button"
                onClick={loadHistory}
                disabled={historyLoading}
                className="text-[11px] text-brand-deep hover:underline disabled:text-slate-400"
                data-testid="upload-links-history-refresh"
              >
                {historyLoading ? 'Cargando…' : 'Actualizar'}
              </button>
            </div>
            {history.length === 0 ? (
              <p className="text-[11px] text-slate-400">
                Aún no hay enlaces generados para este paciente.
              </p>
            ) : (
              <ul className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {history.map((h) => {
                  const stateMap = {
                    sent: { label: 'Enviado', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
                    opened: { label: 'Abierto', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                    uploaded: { label: `${h.files_count} archivo(s)`, cls: 'bg-brand-soft/60 text-brand-deep border-brand-medium' },
                    notified: { label: 'Aviso recibido', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                    expired: { label: 'Caducado', cls: 'bg-red-50 text-red-600 border-red-200' },
                  };
                  const st = stateMap[h.state] || stateMap.sent;
                  const channelIcon = h.channel === 'email' ? <Mail size={11} /> :
                                       h.channel === 'whatsapp' ? <MessageCircle size={11} /> :
                                       <QrCode size={11} />;
                  const created = h.created_at ? new Date(h.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                  return (
                    <li
                      key={h.token}
                      className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
                      data-testid={`upload-link-${h.token.slice(0,8)}`}
                    >
                      <span className="w-5 h-5 flex items-center justify-center rounded-sm bg-brand-soft/40 text-brand-deep flex-shrink-0">
                        {channelIcon}
                      </span>
                      <span className="flex-1 min-w-0 text-slate-600 truncate">{created}</span>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-medium ${st.cls}`}>
                        {h.state === 'uploaded' && <FileText size={10} />}
                        {h.state === 'notified' && <Check size={10} />}
                        {st.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
